import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { cn } from "./cn";

export interface EditableTitleProps {
  readonly value: string | null;
  readonly onCommit: (name: string) => void;
  readonly placeholder?: string;
  readonly className?: string;
}

export function EditableTitle({
  value,
  onCommit,
  placeholder = "Untitled",
  className,
}: EditableTitleProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = useCallback(() => {
    setDraft(value ?? "");
    setEditing(true);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      const caret = input.value.length;
      input.setSelectionRange(caret, caret);
    }
  }, [editing]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed !== (value ?? "")) {
      onCommit(trimmed);
    }
  }, [draft, value, onCommit]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid="editable-title-input"
        className={cn(
          "w-full bg-transparent text-[15px] font-semibold text-content outline-none",
          className,
        )}
        value={draft}
        onChange={(e) => { setDraft(e.target.value); }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        autoFocus
      />
    );
  }

  return (
    <span className={cn("cursor-pointer", className)} onClick={startEditing} data-testid="editable-title">
      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {value || placeholder}
    </span>
  );
}
